import { Schema } from "effect"
import { HttpApiEndpoint, HttpApiGroup, HttpApiSchema } from "effect/http-api"
import { Authentication } from "./AuthApi.ts"
import { UnknownInstrument, UnknownStrategy } from "./Errors.ts"
import { InstrumentId, Timeframe } from "./MarketData.ts"
import { EpochSeconds, RunnerDirection, StrategyOverrides, StrategyRef } from "./Money.ts"

// Backtests on the server (docs/design/0012, phase 2): any strategy on stored bars,
// the same engine and numbers as the Strategy Tester, kept to read again later.

/** At most this many bars per backtest; a longer range is refused. */
export const MAX_BACKTEST_BARS = 100_000

export const BacktestId = Schema.String.pipe(Schema.check(Schema.isPattern(/^bt_[A-Za-z0-9_-]{8,40}$/)), Schema.brand("BacktestId"))
export type BacktestId = typeof BacktestId.Type

export const RunBacktest = Schema.Struct({
  /** "builtin:ma-cross", or a saved strategy: "user:<id>" (its latest version, pinned) or "user:<id>@<v>". */
  strategy: StrategyRef,
  instrumentId: InstrumentId,
  timeframe: Timeframe,
  /** The first bar's earliest open (epoch seconds). */
  from: EpochSeconds,
  /** Bars opening before this (epoch seconds); default now. Only closed bars run. */
  to: Schema.optionalKey(EpochSeconds),
  inputs: Schema.optionalKey(StrategyOverrides),
  /** strategy() property overrides (capital, sizing, commission…); the currency is the symbol's. */
  properties: Schema.optionalKey(StrategyOverrides),
  /** Default all; "long" is allow_entry_in(long): a short entry only closes the long position. */
  direction: Schema.optionalKey(RunnerDirection)
})
export type RunBacktest = typeof RunBacktest.Type

const Num = Schema.NullOr(Schema.Number)

/** One side's results (all trades, longs or shorts); money in `currency`. */
export const BacktestMetrics = Schema.Struct({
  netProfit: Num,
  netProfitPercent: Num,
  grossProfit: Num,
  grossLoss: Num,
  commissionPaid: Num,
  profitFactor: Num,
  totalTrades: Schema.Number,
  openTrades: Schema.Number,
  winningTrades: Schema.Number,
  losingTrades: Schema.Number,
  percentProfitable: Num,
  avgTrade: Num,
  avgTradePercent: Num,
  largestWin: Num,
  largestLoss: Num,
  avgBarsInTrade: Num
})
export type BacktestMetrics = typeof BacktestMetrics.Type

export const BacktestTrade = Schema.Struct({
  direction: Schema.Literals(["long", "short"]),
  entryId: Schema.String,
  entryTime: Schema.Number,
  entryPrice: Num,
  entrySignal: Schema.String,
  /** An open trade is marked to the last close. */
  exitTime: Schema.Number,
  exitPrice: Num,
  exitSignal: Schema.String,
  open: Schema.Boolean,
  qty: Num,
  /** Net of commission. */
  profit: Num,
  profitPercent: Num,
  commission: Num
})
export type BacktestTrade = typeof BacktestTrade.Type

export const BacktestSummary = Schema.Struct({
  id: BacktestId,
  createdAt: EpochSeconds,
  /** Pinned: a user strategy as "user:<id>@<v>". */
  strategy: StrategyRef,
  strategyName: Schema.String,
  instrumentId: InstrumentId,
  timeframe: Timeframe,
  /** The first and last bar's open time. */
  from: Schema.Number,
  to: Schema.Number,
  bars: Schema.Int,
  currency: Schema.String,
  netProfit: Num,
  netProfitPercent: Num,
  totalTrades: Schema.Number,
  percentProfitable: Num,
  profitFactor: Num,
  maxDrawdownPercent: Num
})
export type BacktestSummary = typeof BacktestSummary.Type

export const Backtest = Schema.Struct({
  summary: BacktestSummary,
  /** The request as run (the strategy pinned). */
  request: RunBacktest,
  metrics: Schema.Struct({ all: BacktestMetrics, long: BacktestMetrics, short: BacktestMetrics }),
  maxDrawdown: Num,
  maxDrawdownPercent: Num,
  openProfit: Num,
  buyHoldReturn: Num,
  buyHoldReturnPercent: Num,
  /** Closed trades first (chronological), then open ones; the first 1,000. */
  trades: Schema.Array(BacktestTrade),
  tradesTruncated: Schema.Boolean,
  /** Equity at bar closes, sampled to at most 500 points (the last bar always). */
  equity: Schema.Array(Schema.Struct({ time: Schema.Number, equity: Num })),
  /** The strategy() properties it ran with (its defaults plus the overrides). */
  properties: Schema.Record(Schema.String, Schema.Unknown),
  elapsedMs: Schema.Number
})
export type Backtest = typeof Backtest.Type

/** The range holds more bars than one backtest may run. */
export class BacktestTooLarge extends Schema.TaggedError<BacktestTooLarge>()(
  "BacktestTooLarge",
  { bars: Schema.Int, limit: Schema.Int },
  { httpApiStatus: 422 }
) {}

/** The backtest couldn't run: no bars stored, the strategy threw or ran too long, … */
export class BacktestFailed extends Schema.TaggedError<BacktestFailed>()(
  "BacktestFailed",
  { message: Schema.String },
  { httpApiStatus: 422 }
) {}

export class BacktestNotFound extends Schema.TaggedError<BacktestNotFound>()(
  "BacktestNotFound",
  { id: Schema.String },
  { httpApiStatus: 404 }
) {}

export class BacktestsApi extends HttpApiGroup.make("backtests")
  .add(
    /** Runs a backtest and keeps it (the latest 200 per user). */
    HttpApiEndpoint.post("run", "/", {
      payload: RunBacktest,
      success: Backtest.pipe(HttpApiSchema.status(201)),
      error: [UnknownStrategy, UnknownInstrument, BacktestTooLarge, BacktestFailed]
    }),
    /** The caller's backtests, newest first. */
    HttpApiEndpoint.get("list", "/", { success: Schema.Array(BacktestSummary) }),
    HttpApiEndpoint.get("get", "/:id", { params: { id: BacktestId }, success: Backtest, error: BacktestNotFound })
  )
  .middleware(Authentication)
  .prefix("/backtests")
{}
